import React, { useEffect, useCallback, useContext } from 'react';

import { SpotlightPulse } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useLoomScriptReadTimeContext } from '../context/LoomScriptReadTimeContext';

interface LoomScriptReadTimeButtonNudgeWrapperProps {
	children?: Readonly<React.ReactNode>;
	/*
	 * Required for compatiability with `cc_page_experiences_new_editor_byline` off.
	 * See https://hello.jira.atlassian.cloud/browse/MLTP-2725
	 */
	isAbbreviatedReadTime?: boolean;
	/**
	 * Used for analytics purposes only and is not used in any logic
	 *
	 * This uses minutes instead of seconds unlike `LoomScriptExtensionSpotlightWrapper` because
	 * the parent `ReadTimePopupExperiment` only has the raw minute values
	 */
	readTimeMinutes: number | null;
}

const styles = cssMap({
	/*
	 * Required for compatiability with `cc_page_experiences_new_editor_byline` off.
	 * See https://hello.jira.atlassian.cloud/browse/MLTP-2725
	 */
	baseNudgeContainer: {
		display: 'inline-block',
	},
	nonAbbreviatedReadTimeNudgeContainer: {
		position: 'relative',
		paddingLeft: token('space.250'),
	},
});

export const LoomScriptReadTimeButtonNudgeWrapper = ({
	children,
	/**
	 * Copying logic in `next/packages/read-time/src/ReadTimeComponent.tsx`
	 */
	isAbbreviatedReadTime = fg('cc_page_experiences_new_editor_byline'),
	readTimeMinutes,
}: LoomScriptReadTimeButtonNudgeWrapperProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLoomScriptReadTimeButtonNudgeShown, hideLoomScriptReadTimeNudgeAndShowSpotlight } =
		useLoomScriptReadTimeContext();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { entryPointVariant } = useLoomEntryPointVariant();

	// only actually show the nudge if we are in the variation cohort
	const showNudge =
		isLoomScriptReadTimeButtonNudgeShown &&
		expValEquals('loom_ai_script_for_confluence_page_experiment', 'cohort', 'variation');

	const handleLoomScriptReadTimeButtonNudgeRendered = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'loomScriptReadTimeButtonNudge',
				product: 'confluence',
				source: 'loomScriptReadTimeButtonNudge',
				attributes: {
					entryPointVariant,
					readTimeMinutes,
					isSiteAdmin,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, entryPointVariant, readTimeMinutes, isSiteAdmin]);

	const handleLoomScriptReadTimeButtonNudgeClicked = useCallback(() => {
		/**
		 * Dismissal should come from the spotlight that is shown after the nudge button click
		 */
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'loomScriptReadTimeButtonNudge',
				product: 'confluence',
				source: 'loomScriptReadTimeButtonNudge',
				attributes: {
					entryPointVariant,
					readTimeMinutes,
					isSiteAdmin,
				},
			},
		}).fire();
		if (showNudge) {
			hideLoomScriptReadTimeNudgeAndShowSpotlight();
		}
	}, [
		createAnalyticsEvent,
		hideLoomScriptReadTimeNudgeAndShowSpotlight,
		showNudge,
		entryPointVariant,
		readTimeMinutes,
		isSiteAdmin,
	]);

	useEffect(() => {
		if (showNudge) {
			handleLoomScriptReadTimeButtonNudgeRendered();
		}
	}, [handleLoomScriptReadTimeButtonNudgeRendered, showNudge]);

	return (
		<Box
			xcss={cx(
				styles.baseNudgeContainer,
				/**
				 * Need special styling to accomodate for the :before element positioned absolutely
				 */
				!isAbbreviatedReadTime && styles.nonAbbreviatedReadTimeNudgeContainer,
			)}
		>
			<SpotlightPulse
				onClick={handleLoomScriptReadTimeButtonNudgeClicked}
				pulse={showNudge}
				radius={4}
			>
				{children}
			</SpotlightPulse>
		</Box>
	);
};
